import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, ArrowRight, FileDown, Loader2 } from "lucide-react";

/* /caregiver-tools — public library for the Facebook series
 * "30 Days of Things Nobody Tells You About Dementia Care".
 * No login, no email gate. Only published tools are shown. */
export default function CaregiverTools() {
    const [state, setState] = useState({ loading: true, tools: [] });
    const { user } = useAuth();

    useEffect(() => {
        api.get("/caregiver-tools")
            .then(r => setState({ loading: false, tools: r.data.tools || [] }))
            .catch(() => setState({ loading: false, tools: [] }));
        track("caregiver_tools_page_view", { referrer: document.referrer || "" });
    }, []);

    return (
        <main className="min-h-screen bg-cream">
            <SeoHead
                title="Free Dementia Caregiver Tools — Memory Lane"
                description="Practical free tools to help you feel more prepared, keep important information together, and navigate dementia care one day at a time. No signup required."
                canonicalPath="/caregiver-tools"
                image={`${window.location.origin}/og/caregiver-tools.png`}
                imageAlt="Free Dementia Caregiver Tools from Memory Lane"
            />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14">
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark mb-6" data-testid="caregiver-tools-back">
                    <ArrowLeft size={15} /> Memory Lane home
                </Link>
                <header className="mb-10">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2">Free — no account needed</p>
                    <h1 className="text-4xl sm:text-5xl font-heading font-semibold tracking-tight text-[#12776F]" data-testid="caregiver-tools-title">
                        Free Dementia Caregiver Tools
                    </h1>
                    <p className="mt-3 text-base md:text-lg text-[#4C5551] leading-relaxed max-w-2xl">
                        Practical tools to help you feel more prepared, keep important information together, and navigate dementia care one day at a time.
                    </p>
                    <p className="mt-2 text-sm text-[#6E7A74] italic">
                        Part of Memory Lane&apos;s &ldquo;30 Days of Things Nobody Tells You About Dementia Care.&rdquo;
                    </p>
                </header>

                {state.loading ? (
                    <div className="flex items-center gap-2 text-[#6E7A74] py-12" data-testid="caregiver-tools-loading">
                        <Loader2 size={18} className="animate-spin" /> Loading tools…
                    </div>
                ) : state.tools.length === 0 ? (
                    <div className="ml-card p-8 text-center text-sm text-[#6E7A74]" data-testid="caregiver-tools-empty">
                        The first tool is on its way — check back today.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="caregiver-tools-grid">
                        {state.tools.map(t => (
                            <Link key={t.slug} to={`/caregiver-tools/${t.slug}`}
                                className="ml-card p-6 group hover:border-teal transition-colors flex flex-col"
                                data-testid={`caregiver-tool-card-${t.slug}`}>
                                <span className="inline-flex self-start items-center rounded-full bg-teal-dark text-white text-[11px] font-semibold tracking-wider px-3 py-1 mb-3">
                                    DAY {t.day}
                                </span>
                                <h2 className="font-heading text-lg font-semibold text-[#1F2624] group-hover:text-teal-dark transition-colors">{t.title}</h2>
                                <p className="text-sm text-[#4C5551] mt-1.5 leading-relaxed flex-1">
                                    {t.day === 1 ? "Keep the important information you find yourself repeating in one place." : t.short_description}
                                </p>
                                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-dark">
                                    View tool <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                                </span>
                            </Link>
                        ))}
                    </div>
                )}

                <div className="mt-10 rounded-3xl border border-cream-dark bg-white p-6 flex flex-wrap items-center justify-between gap-4" data-testid="caregiver-tools-footer-cta">
                    <div>
                        <p className="font-heading font-semibold text-[#1F2624]">A new tool arrives every day for 30 days.</p>
                        <p className="text-sm text-[#6E7A74] mt-0.5 inline-flex items-center gap-1.5"><FileDown size={14} /> Every printable is free — no email, no signup, no catch.</p>
                    </div>
                    <Link to={user && user.id ? "/dashboard" : "/login?utm_campaign=caregiver-tools"}
                        className="ml-btn-secondary" data-testid="caregiver-tools-signup-cta"
                        onClick={() => track("caregiver_tool_digital_cta_click", { tool_day: 0, tool_name: "library_footer", referrer: document.referrer || "" })}>
                        {user && user.id ? "Open Memory Lane" : "Try Memory Lane free"}
                    </Link>
                </div>
            </div>
        </main>
    );
}
